/**
 * WF-Badge - Keyboard shortcut badge component for wizard forms
 *
 * A reusable badge component with multiple variants for displaying keyboard shortcuts.
 *
 * @example
 * <wf-badge shortcut="A"></wf-badge>
 * <wf-badge shortcut="Enter" variant="selected"></wf-badge>
 * <wf-badge shortcut="↵" variant="button"></wf-badge>
 */

import { LitElement, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';

import { wfBadgeStyles } from './wf-badge.styles.js';

export type BadgeVariant = 'default' | 'selected' | 'button' | 'button-secondary';

@customElement('wf-badge')
export class WfBadge extends LitElement {
  static styles = wfBadgeStyles;

  /**
   * The text to display in the badge (e.g., 'A', 'Enter', '↵')
   */
  @property({ type: String })
  shortcut = '';

  /**
   * Visual variant of the badge
   */
  @property({ type: String })
  variant: BadgeVariant = 'default';

  render() {
    return html`
      <span
        class="wf-badge wf-badge--${this.variant}"
        aria-hidden="true"
        data-testid="wf-badge"
      >
        ${this.shortcut}
      </span>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'wf-badge': WfBadge;
  }
}
